import { useEffect, useState, useCallback } from "react";
import { appConfirm } from "../../components/ConfirmDialog";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Ticket, Plus, Trash2, Pencil, X, Check, TrendingUp } from "lucide-react";

const EMPTY = { code: "", type: "pct", value: "", valid_from: "", valid_to: "", max_uses: 0, terms: "", active: true };
const gbp = (n) => `£${Math.round(Number(n) || 0).toLocaleString("en-GB")}`;

export function DiscountCodesPanel() {
  const [codes, setCodes] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/admin/discount-codes"); setCodes(r.data.codes || []); }
    catch { toast.error("Could not load discount codes"); }
    try { const a = await api.get("/admin/discount-codes/analytics"); setAnalytics(a.data); }
    catch (e) { console.debug("Discount analytics unavailable:", e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const reset = () => { setForm(EMPTY); setEditingId(null); };
  const edit = (c) => { setEditingId(c.id); setForm({ ...EMPTY, ...c, value: String(c.value), max_uses: c.max_uses || 0 }); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const save = async () => {
    if (!form.code.trim() || !form.value) { toast.error("Enter a code and a value"); return; }
    setSaving(true);
    const payload = { ...form, value: parseFloat(form.value), max_uses: parseInt(form.max_uses) || 0 };
    try {
      if (editingId) { await api.put(`/admin/discount-codes/${editingId}`, payload); toast.success("Code updated"); }
      else { await api.post("/admin/discount-codes", payload); toast.success("Code created"); }
      reset(); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save code"); }
    setSaving(false);
  };
  const remove = async (id) => {
    if (!(await appConfirm({ title: "Delete this discount code?", confirmLabel: "Delete", destructive: true }))) return;
    try { await api.delete(`/admin/discount-codes/${id}`); toast.success("Code deleted"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };
  const toggleActive = async (c) => {
    try { await api.put(`/admin/discount-codes/${c.id}`, { ...c, active: !c.active }); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  const fmtVal = (c) => c.type === "pct" ? `${c.value}%` : `£${Number(c.value).toLocaleString("en-GB")}`;
  const fmtWindow = (c) => (c.valid_from || c.valid_to) ? `${c.valid_from || "—"} → ${c.valid_to || "—"}` : "Always";

  return (
    <div data-testid="discount-codes-panel">
      <div className="flex items-center gap-2 mb-1">
        <Ticket size={18} className="text-[#C79A3B]" />
        <h2 className="text-xl font-black tracking-tight">Discount codes</h2>
      </div>
      <p className="text-sm text-[#6B7280] mb-5 max-w-2xl">Codes clients enter on the booking step. Validity is checked against the booking date. A code overrides any automatic offer.</p>

      <div className="border border-[#E5E7EB] bg-white p-4 mb-6">
        <div className="label-eyebrow mb-3">{editingId ? "Edit code" : "Create a new code"}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <label className="text-xs text-[#6B7280]">Code
            <input data-testid="dc-code" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="SUMMER10" className="border border-[#E5E7EB] px-3 py-2 text-sm w-full mt-1 uppercase" />
          </label>
          <label className="text-xs text-[#6B7280]">Type
            <select data-testid="dc-type" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="border border-[#E5E7EB] px-3 py-2 text-sm w-full mt-1 bg-white">
              <option value="pct">Percentage %</option><option value="gbp">Fixed £</option>
            </select>
          </label>
          <label className="text-xs text-[#6B7280]">Value
            <input data-testid="dc-value" type="number" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} placeholder={form.type === "pct" ? "10" : "500"} className="border border-[#E5E7EB] px-3 py-2 text-sm w-full mt-1" />
          </label>
          <label className="text-xs text-[#6B7280]">Max uses (0 = unlimited)
            <input data-testid="dc-maxuses" type="number" value={form.max_uses} onChange={e => setForm({ ...form, max_uses: e.target.value })} className="border border-[#E5E7EB] px-3 py-2 text-sm w-full mt-1" />
          </label>
          <label className="text-xs text-[#6B7280]">Valid from (booking date)
            <input data-testid="dc-from" type="date" value={form.valid_from || ""} onChange={e => setForm({ ...form, valid_from: e.target.value })} className="border border-[#E5E7EB] px-3 py-2 text-sm w-full mt-1" />
          </label>
          <label className="text-xs text-[#6B7280]">Valid to (booking date)
            <input data-testid="dc-to" type="date" value={form.valid_to || ""} onChange={e => setForm({ ...form, valid_to: e.target.value })} className="border border-[#E5E7EB] px-3 py-2 text-sm w-full mt-1" />
          </label>
          <label className="text-xs text-[#6B7280] lg:col-span-2 flex items-end gap-2 pb-2">
            <input data-testid="dc-active" type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} /> Active
          </label>
          <label className="text-xs text-[#6B7280] sm:col-span-2 lg:col-span-4">Terms &amp; conditions (shown to the client)
            <textarea data-testid="dc-terms" value={form.terms} onChange={e => setForm({ ...form, terms: e.target.value })} rows={2} placeholder="e.g. Valid on new bookings only. Cannot be combined with other offers." className="border border-[#E5E7EB] px-3 py-2 text-sm w-full mt-1" />
          </label>
        </div>
        <div className="flex gap-2 mt-3">
          <button data-testid="dc-save" onClick={save} disabled={saving} className="de-btn-primary text-sm disabled:opacity-60">
            {editingId ? <><Check size={15} /> Update code</> : <><Plus size={15} /> Create code</>}
          </button>
          {editingId && <button data-testid="dc-cancel" onClick={reset} className="de-btn-secondary text-sm"><X size={15} /> Cancel</button>}
        </div>
      </div>

      {loading ? <div className="label-eyebrow">Loading…</div> : (
        <div className="border border-[#E5E7EB] bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="dc-table">
            <thead><tr className="bg-[#12263A] text-white text-left text-xs uppercase tracking-wide">
              <th className="px-3 py-2">Code</th><th className="px-3 py-2">Discount</th><th className="px-3 py-2">Valid window</th>
              <th className="px-3 py-2 text-right">Uses</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th>
            </tr></thead>
            <tbody>
              {codes.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-[#9CA3AF]">No discount codes yet.</td></tr>}
              {codes.map(c => (
                <tr key={c.id} data-testid={`dc-row-${c.code}`} className="border-b border-[#F0EEE9]">
                  <td className="px-3 py-2 font-mono font-bold">{c.code}</td>
                  <td className="px-3 py-2">{fmtVal(c)}</td>
                  <td className="px-3 py-2 text-[#6B7280]">{fmtWindow(c)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{c.used_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                  <td className="px-3 py-2">
                    <button data-testid={`dc-toggle-${c.code}`} onClick={() => toggleActive(c)} className={`text-xs font-bold uppercase tracking-wider px-2 py-1 ${c.active ? "bg-[#E7F4EC] text-[#1F6B3B]" : "bg-[#F3F4F6] text-[#9CA3AF]"}`}>{c.active ? "Active" : "Off"}</button>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button data-testid={`dc-edit-${c.code}`} onClick={() => edit(c)} className="text-[#6B7280] hover:text-[#12263A] p-1"><Pencil size={15} /></button>
                    <button data-testid={`dc-delete-${c.code}`} onClick={() => remove(c.id)} className="text-[#6B7280] hover:text-[#C2401F] p-1 ml-1"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {analytics && (
        <div className="mt-10" data-testid="dc-analytics">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={18} className="text-[#C79A3B]" />
            <h2 className="text-xl font-black tracking-tight">Redemption analytics</h2>
          </div>
          <p className="text-sm text-[#6B7280] mb-5 max-w-2xl">How each code is performing — bookings it was used on, the revenue those bookings are worth, and the discount you gave away to win them.</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="border border-[#E5E7EB] bg-white p-4" data-testid="dc-stat-redemptions">
              <div className="text-[10px] uppercase tracking-widest text-[#6B7280]">Bookings with a code</div>
              <div className="text-2xl font-black mt-1">{analytics.totals?.bookings || 0}</div>
              <div className="text-[11px] text-[#6B7280] mt-0.5">{analytics.totals?.paid_bookings || 0} paid a deposit</div>
            </div>
            <div className="border border-[#E5E7EB] bg-white p-4" data-testid="dc-stat-revenue">
              <div className="text-[10px] uppercase tracking-widest text-[#6B7280]">Revenue won (paid)</div>
              <div className="text-2xl font-black mt-1 text-[#1F6B3B]">{gbp(analytics.totals?.revenue)}</div>
              <div className="text-[11px] text-[#6B7280] mt-0.5">contract value of paid bookings</div>
            </div>
            <div className="border border-[#E5E7EB] bg-white p-4" data-testid="dc-stat-pipeline">
              <div className="text-[10px] uppercase tracking-widest text-[#6B7280]">In the pipeline</div>
              <div className="text-2xl font-black mt-1">{gbp(analytics.totals?.pipeline_revenue)}</div>
              <div className="text-[11px] text-[#6B7280] mt-0.5">booked with a code, deposit pending</div>
            </div>
            <div className="border border-[#E5E7EB] bg-white p-4" data-testid="dc-stat-discount">
              <div className="text-[10px] uppercase tracking-widest text-[#6B7280]">Discount given</div>
              <div className="text-2xl font-black mt-1 text-[#B45309]">{gbp(analytics.totals?.discount_given)}</div>
              <div className="text-[11px] text-[#6B7280] mt-0.5">total value knocked off quotes</div>
            </div>
          </div>
          <div className="border border-[#E5E7EB] bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="dc-analytics-table">
              <thead><tr className="bg-[#12263A] text-white text-left text-xs uppercase tracking-wide">
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2 text-right">Bookings</th>
                <th className="px-3 py-2 text-right">Paid</th>
                <th className="px-3 py-2 text-right">Revenue (paid)</th>
                <th className="px-3 py-2 text-right">Pipeline</th>
                <th className="px-3 py-2 text-right">Discount given</th>
                <th className="px-3 py-2">Last used</th>
              </tr></thead>
              <tbody>
                {(analytics.codes || []).length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-[#9CA3AF]">No redemptions yet — analytics appear as soon as a code is used on a booking.</td></tr>}
                {(analytics.codes || []).map(c => (
                  <tr key={c.code} data-testid={`dc-analytics-row-${c.code}`} className="border-b border-[#F0EEE9]">
                    <td className="px-3 py-2 font-mono font-bold">{c.code}{c.deleted && <span className="ml-1.5 text-[9px] uppercase tracking-wide text-[#9CA3AF] bg-[#F0EFEC] px-1">deleted</span>}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{c.bookings || 0}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{c.paid_bookings || 0}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-[#1F6B3B]">{gbp(c.revenue)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[#6B7280]">{gbp(c.pipeline_revenue)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[#B45309]">{gbp(c.discount_given)}</td>
                    <td className="px-3 py-2 text-[#6B7280]">{c.last_used ? new Date(c.last_used).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
